/**
 * OAuthConsentPage.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * OAuth 2.0 PKCE consent screen — shown when an AI client (ChatGPT, Gemini,
 * Claude) requests access to a user's Lusuku account.
 *
 * Route: /oauth/consent
 *
 * Query params injected by GET /oauth/authorize on the backend:
 *   client_id, client_name, logo_url, redirect_uri, scope, scope_labels,
 *   state, code_challenge, code_challenge_method, session (Lusuku JWT)
 *
 * On Allow:  POST /oauth/authorize/approve → redirects to AI client's redirect_uri?code=xxx
 * On Deny:   redirects to redirect_uri?error=access_denied&state=xxx
 */

import React, { useState } from 'react';
import { useLocation, useHistory }     from 'react-router-dom';
import axios                           from 'axios';
import { SEOHead }                     from '../components/SEO/SEOHead';
import SearchIcon      from '@material-ui/icons/Search';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import LockIcon        from '@material-ui/icons/Lock';
import { SITE_ORIGIN } from '../config/market';

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8001';

// ── Scope icon map ────────────────────────────────────────────────────────────
const SCOPE_ICONS = {
  'catalog:read':  <SearchIcon style={{ fontSize: 18, color: '#6366f1' }} />,
  'orders:write':  <ShoppingCartIcon style={{ fontSize: 18, color: '#16a34a' }} />,
  'orders:read':   <LocalShippingIcon style={{ fontSize: 18, color: '#2563eb' }} />,
  'profile:read':  <PersonOutlineIcon style={{ fontSize: 18, color: '#9333ea' }} />,
};

// ── Sub-components ────────────────────────────────────────────────────────────

function ScopeItem({ scope, label }) {
  return (
    <div style={styles.scopeItem}>
      <span style={styles.scopeIcon}>{SCOPE_ICONS[scope] || <CheckCircleOutlineIcon style={{ fontSize: 18, color: '#16a34a' }} />}</span>
      <span style={styles.scopeLabel}>{label}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const OAuthConsentPage = () => {
  const location = useLocation();
  const history  = useHistory();

  const params  = new URLSearchParams(location.search);
  const clientId             = params.get('client_id')             || '';
  const clientName           = params.get('client_name')           || 'An AI application';
  const logoUrl              = params.get('logo_url')              || '';
  const redirectUri          = params.get('redirect_uri')          || '';
  const scope                = params.get('scope')                 || 'catalog:read';
  const state                = params.get('state')                 || '';
  const codeChallenge        = params.get('code_challenge')        || '';
  const codeChallengeMethod  = params.get('code_challenge_method') || 'S256';
  const session              = params.get('session')               || '';

  // Parse scope labels JSON (set by backend authorize handler)
  let scopeLabels = {};
  try {
    scopeLabels = JSON.parse(params.get('scope_labels') || '{}');
  } catch { /* use raw scope strings as fallback */ }

  const scopeList = scope.split(' ').filter(Boolean);

  const [loading, setLoading]     = useState(false);
  const [error,   setError]       = useState('');
  const [logoFailed, setLogoFailed] = useState(false);

  // Guard: if required params are missing, show an error immediately
  const paramsValid = clientId && redirectUri;

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleAllow = async () => {
    setLoading(true);
    setError('');
    try {
      // POST to backend — returns JSON { success: true, redirectTo: <chatgpt callback URL> }
      const body = {
        client_id:    clientId,
        redirect_uri: redirectUri,
        scope,
        state,
        token:        session,
      };
      // Only include PKCE fields when a real code_challenge exists
      if (codeChallenge) {
        body.code_challenge        = codeChallenge;
        body.code_challenge_method = codeChallengeMethod;
      }
      const response = await axios.post(
        `${BASE_URL}/oauth/authorize/approve`,
        body
      );

      const redirectTo = response.data?.redirectTo;
      if (redirectTo) {
        window.location.href = redirectTo;
      } else {
        setError('Unexpected response from server. Please try again.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Authorization failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeny = () => {
    if (!redirectUri) { history.push('/'); return; }
    const denyUrl = new URL(redirectUri);
    denyUrl.searchParams.set('error', 'access_denied');
    denyUrl.searchParams.set('error_description', 'User denied access');
    if (state) denyUrl.searchParams.set('state', state);
    window.location.href = denyUrl.toString();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!paramsValid) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h2 style={styles.title}>Invalid Request</h2>
          <p style={styles.subtitle}>
            This authorization link is missing required parameters.
            Please return to the app and try again.
          </p>
          <button style={styles.btnDeny} onClick={() => history.push('/')}>
            Go to Lusuku
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Authorize Access"
        description={`${clientName} is requesting access to your Lusuku account.`}
        canonicalPath="/oauth/consent"
      />

      <div style={styles.page}>
        <div style={styles.card}>
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div style={styles.header}>
            <img
              src={`${SITE_ORIGIN}/logo512.png`}
              alt="Lusuku"
              style={styles.lusukuLogo}
            />
            <div style={styles.arrowBadge}>→</div>
            {logoUrl && !logoFailed
              ? <img src={logoUrl} alt={clientName} style={styles.clientLogo} onError={() => setLogoFailed(true)} />
              : <div style={styles.clientLogoPlaceholder}>{clientName[0]}</div>
            }
          </div>

          {/* ── App name + description ──────────────────────────────────────── */}
          <h2 style={styles.title}>
            <span style={styles.clientNameSpan}>{clientName}</span> wants access
          </h2>
          <p style={styles.subtitle}>
            This AI assistant is requesting the following permissions on your
            Lusuku account:
          </p>

          {/* ── Scope list ─────────────────────────────────────────────────── */}
          <div style={styles.scopeList}>
            {scopeList.map(s => (
              <ScopeItem
                key={s}
                scope={s}
                label={scopeLabels[s] || s}
              />
            ))}
          </div>

          {/* ── Security note ───────────────────────────────────────────────── */}
          <p style={styles.securityNote}>
            <LockIcon style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 4, color: '#6b7280' }} />
            <strong>{clientName}</strong> will never see your password.
            You can revoke access at any time from{' '}
            <a href="/my_profile/connected_apps" style={styles.policyLink}>
              Profile → Connected Apps
            </a>.
            Your existing order history and account remain yours — this just
            lets the AI assistant shop on your behalf.
          </p>

          {/* ── Error message ───────────────────────────────────────────────── */}
          {error && <div style={styles.errorBox}>{error}</div>}

          {/* ── Action buttons ──────────────────────────────────────────────── */}
          <div style={styles.btnRow}>
            <button
              style={styles.btnDeny}
              onClick={handleDeny}
              disabled={loading}
            >
              Deny
            </button>
            <button
              style={{ ...styles.btnAllow, opacity: loading ? 0.7 : 1 }}
              onClick={handleAllow}
              disabled={loading}
            >
              {loading ? 'Authorizing…' : 'Allow'}
            </button>
          </div>

          {/* ── Policy link ─────────────────────────────────────────────────── */}
          <p style={styles.policyNote}>
            By allowing, you agree to Lusuku's{' '}
            <a href="/policy" style={styles.policyLink}>Privacy Policy</a>.
          </p>
        </div>
      </div>
    </>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight:       '100vh',
    backgroundColor: '#f4f6f9',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         '24px 16px',
    fontFamily:      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius:    '16px',
    boxShadow:       '0 4px 24px rgba(0,0,0,0.10)',
    padding:         '36px 32px',
    maxWidth:        '440px',
    width:           '100%',
    textAlign:       'center',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '12px',
    marginBottom:   '24px',
  },
  lusukuLogo: {
    width:        '52px',
    height:       '52px',
    borderRadius: '12px',
    objectFit:    'cover',
  },
  arrowBadge: {
    fontSize:   '20px',
    color:      '#aaa',
    fontWeight: '600',
  },
  clientLogo: {
    width:        '52px',
    height:       '52px',
    borderRadius: '12px',
    objectFit:    'cover',
    border:       '1px solid #e5e7eb',
  },
  clientLogoPlaceholder: {
    width:           '52px',
    height:          '52px',
    borderRadius:    '12px',
    backgroundColor: '#6366f1',
    color:           '#fff',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    fontSize:        '22px',
    fontWeight:      '700',
  },
  title: {
    fontSize:     '20px',
    fontWeight:   '700',
    color:        '#111827',
    margin:       '0 0 8px',
    lineHeight:   1.3,
  },
  clientNameSpan: {
    color: '#16a34a',
  },
  subtitle: {
    fontSize:     '14px',
    color:        '#6b7280',
    margin:       '0 0 20px',
    lineHeight:   1.5,
  },
  scopeList: {
    backgroundColor: '#f9fafb',
    borderRadius:    '10px',
    padding:         '12px 16px',
    marginBottom:    '20px',
    textAlign:       'left',
  },
  scopeItem: {
    display:      'flex',
    alignItems:   'center',
    gap:          '10px',
    padding:      '6px 0',
    borderBottom: '1px solid #e5e7eb',
  },
  scopeIcon: {
    fontSize:   '18px',
    flexShrink: 0,
  },
  scopeLabel: {
    fontSize: '14px',
    color:    '#374151',
  },
  securityNote: {
    fontSize:    '12px',
    color:       '#6b7280',
    margin:      '0 0 16px',
    lineHeight:  1.5,
    textAlign:   'left',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border:          '1px solid #fca5a5',
    borderRadius:    '8px',
    padding:         '10px 14px',
    fontSize:        '13px',
    color:           '#b91c1c',
    marginBottom:    '16px',
    textAlign:       'left',
  },
  btnRow: {
    display:        'flex',
    gap:            '12px',
    marginBottom:   '16px',
  },
  btnDeny: {
    flex:            1,
    padding:         '12px',
    borderRadius:    '8px',
    border:          '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color:           '#374151',
    fontSize:        '15px',
    fontWeight:      '600',
    cursor:          'pointer',
  },
  btnAllow: {
    flex:            2,
    padding:         '12px',
    borderRadius:    '8px',
    border:          'none',
    backgroundColor: '#16a34a',
    color:           '#ffffff',
    fontSize:        '15px',
    fontWeight:      '600',
    cursor:          'pointer',
    transition:      'background-color 0.2s',
  },
  policyNote: {
    fontSize: '12px',
    color:    '#9ca3af',
    margin:   0,
  },
  policyLink: {
    color:          '#16a34a',
    textDecoration: 'underline',
  },
};

export default OAuthConsentPage;
